import axios from "axios";
import dayjs from 'dayjs';

// Gmail OAuth Functions
const getGmailAccessToken = async (authorizationCode) => {
  console.log("🔐 getGmailAccessToken function called");
  console.log("🔐 authorizationCode parameter:", authorizationCode);
  console.log("🔐 authorizationCode type:", typeof authorizationCode);
  console.log("🔐 authorizationCode length:", authorizationCode ? authorizationCode.length : 'null');

  if (!authorizationCode) {
    throw new Error('Authorization code is required');
  }

  const tokenRequestBody = {
    grant_type: "authorization_code",
    code: authorizationCode,
    redirect_uri:`${window.location.origin}/auth/callback`,
    client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
    client_secret: process.env.REACT_APP_GOOGLE_CLIENT_SECRET,
  };

  async function read_gmail_user_info(accessToken) {
    try {
      const response = await axios.get(
        `https://www.googleapis.com/oauth2/v2/userinfo`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      console.log('Gmail user info received:', response?.data);
      
      // Enhance user data with connection timestamp and additional metadata
      const enhancedUserData = {
        ...response?.data,
        lastConnected: new Date().toISOString(),
        connectionStatus: 'active',
        permissions: ['gmail.send', 'userinfo.email'],
        tokenSource: 'oauth2'
      };
      
      localStorage.setItem(
        "gmail_user_data",
        JSON.stringify(enhancedUserData)
      );
      
      // Also store email separately for easier access
      if (response?.data?.email) {
        localStorage.setItem("gmail_user_email", response?.data?.email);
      }
      
      console.log('✅ Gmail user data enhanced and stored successfully');
      return enhancedUserData;
    } catch (e) {
      console.error('Error reading Gmail user info:', e);
      throw e;
    }
  }

  try {
    const response = await axios.post(
      `https://oauth2.googleapis.com/token`,
      tokenRequestBody,
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Basic ${btoa(
            `${process.env.REACT_APP_GOOGLE_CLIENT_ID}:${process.env.REACT_APP_GOOGLE_CLIENT_SECRET}`
          )}`,
        },
      }
    );

    const accessToken = response.data.access_token;
    const refreshToken = response.data.refresh_token;
    
    // Store tokens with enhanced metadata
    localStorage.setItem("gmail_refresh_token", refreshToken);
    localStorage.setItem("gmail_token", accessToken);
    localStorage.setItem("gmail_token_expiry", new Date(Date.now() + (response.data.expires_in * 1000)).toISOString());
    
    console.log('✅ Gmail tokens stored successfully:');
    console.log('  - Access Token: Present');
    console.log('  - Refresh Token: Present');
    console.log('  - Expiry: Set');
    
    // Get and store enhanced user info
    const userData = await read_gmail_user_info(accessToken);
    
    // Dispatch custom event to notify components of successful connection
    const connectionEvent = new CustomEvent('gmailConnected', {
      detail: {
        success: true,
        userData,
        timestamp: new Date().toISOString()
      }
    });
    window.dispatchEvent(connectionEvent);
    
    return response.data;
  } catch (error) {
    console.error('Gmail token exchange error:', error);
    
    if (error.response?.data?.error === "invalid_grant") {
      try {
        const refreshResponse = await axios.post(
          `https://oauth2.googleapis.com/token`,
          `refresh_token=${localStorage.getItem(
            "gmail_refresh_token"
          )}&grant_type=refresh_token&client_id=${
            process.env.REACT_APP_GOOGLE_CLIENT_ID
          }`,
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
              Authorization: `Basic ${btoa(
                `${process.env.REACT_APP_GOOGLE_CLIENT_ID}:${process.env.REACT_APP_GOOGLE_CLIENT_SECRET}`
              )}`,
            },
          }
        );
        
        const accessToken = refreshResponse.data.access_token;
        const refreshToken = refreshResponse.data.refresh_token;
        localStorage.setItem("gmail_refresh_token", refreshToken);
        localStorage.setItem("gmail_token", accessToken);
        
        return refreshResponse.data;
      } catch (refreshError) {
        console.error('Gmail refresh token error:', refreshError);
        throw new Error('Failed to refresh Gmail access token');
      }
    }
    
    throw error;
  }
};

// Outlook OAuth Functions
const getOutlookAccessToken = (newStr) => {
  const tokenRequestBody = {
    grant_type: "authorization_code",
    code: newStr,
    redirect_uri: `${window.location.origin}/setting`,
    client_id: process.env.REACT_APP_OUTLOOK_CLIENT_ID,
    client_secret: process.env.REACT_APP_OUTLOOK_CLIENT_SECRET,
  };

  async function read_outlook_user_info(accessToken) {
    try {
      axios
        .get(
          `https://graph.microsoft.com/v1.0/me`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        )
        .then((res) => {
          console.log(res?.data);
          localStorage.setItem(
            "outlook_user_data",
            JSON.stringify(res?.data)
          );
        });
    } catch (e) {
      return e;
    }
  }

  axios
    .post(
      `https://login.microsoftonline.com/common/oauth2/v2.0/token`,
      tokenRequestBody,
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Basic ${btoa(
            `${process.env.REACT_APP_OUTLOOK_CLIENT_ID}:${process.env.REACT_APP_OUTLOOK_CLIENT_SECRET}`
          )}`,
        },
      }
    )
    .then((response) => {
      const accessToken = response.data.access_token;
      const refreshToken = response.data.refresh_token;
      localStorage.setItem("outlook_refresh_token", refreshToken);
      localStorage.setItem("outlook_token", accessToken);
      read_outlook_user_info(accessToken);
    })
    .catch((error) => {
      if (error.response.data.error === "invalid_grant") {
        axios
          .post(
            `https://login.microsoftonline.com/common/oauth2/v2.0/token`,
            `refresh_token=${localStorage.getItem(
              "outlook_refresh_token"
            )}&grant_type=refresh_token&client_id=${
              process.env.REACT_APP_OUTLOOK_CLIENT_ID
            }`,
            {
              headers: {
                "Content-Type": "application/x-www-form-urlencoded",
                Authorization: `Basic ${btoa(
                  `${process.env.REACT_APP_OUTLOOK_CLIENT_ID}:${process.env.REACT_APP_OUTLOOK_CLIENT_SECRET}`
                )}`,
              },
            }
          )
          .then((response) => {
            const accessToken = response.data.access_token;
            const refreshToken = response.data.refresh_token;
            localStorage.setItem("outlook_refresh_token", refreshToken);
            localStorage.setItem("outlook_token", accessToken);
          });
      }
      return;
    });
};

// Template processing function
const processTemplate = (template, variables = {}) => {
  if (!template) return '';

  console.log('🔄 Processing template in emailService:');
  console.log('  Original template:', template);
  console.log('  Variables:', variables);

  let processedTemplate = template;

  // Replace all variables in the template
  Object.keys(variables).forEach(key => {
    const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
    const value = variables[key] || 'N/A';
    const beforeReplace = processedTemplate;
    processedTemplate = processedTemplate.replace(regex, value);

    if (beforeReplace !== processedTemplate) {
      console.log(`  ✅ Replaced {{${key}}} with: ${value}`);
    } else {
      console.log(`  ❌ No replacement for {{${key}}} - value: ${value}`);
    }
  });

  console.log('  Final processed template:', processedTemplate);
  return processedTemplate;
};

// Email sending functions
const sendEmailViaGmail = async (toEmail, subject, body, attachments = []) => {
  const accessToken = localStorage.getItem("gmail_token");
  
  if (!accessToken) {
    throw new Error("Gmail not configured. Please set up Gmail in Settings.");
  }

  // UTF-8 safe Base64 encoding for Gmail API
  const base64EncodeUnicode = (str) => {
    return btoa(unescape(encodeURIComponent(str)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  };

  // Use notifications@ascendware.net as the sender
  const senderEmail = 'notifications@ascendware.net';
  
  const message = [
    `From: ${senderEmail}`,
    `To: ${toEmail}`,
    `Subject: ${subject}`,
    `Content-Type: text/plain; charset=UTF-8`,
    ``,
    body
  ].join('\n');

  const encodedMessage = base64EncodeUnicode(message);

  console.log('📧 Gmail API Request Details:');
  console.log('  To Email:', toEmail);
  console.log('  Subject:', subject);
  console.log('  Body Length:', body.length);
  console.log('  Access Token Present:', !!accessToken);
  console.log('  Encoded Message Length:', encodedMessage.length);

  try {
    const response = await axios.post(
      'https://gmail.googleapis.com/gmail/v1/users/me/messages/send',
      { raw: encodedMessage },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error('Gmail API Error Details:');
    console.error('  Status:', error.response?.status);
    console.error('  Status Text:', error.response?.statusText);
    console.error('  Data:', error.response?.data);
    console.error('  Message:', error.message);
    console.error('  Full Error:', error);
    
    // Handle token refresh
    if (error.response?.status === 401) {
      const refreshToken = localStorage.getItem("gmail_refresh_token");
      if (refreshToken) {
        try {
          const refreshResponse = await axios.post(
            'https://oauth2.googleapis.com/token',
            {
              client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
              client_secret: process.env.REACT_APP_GOOGLE_CLIENT_SECRET,
              refresh_token: refreshToken,
              grant_type: 'refresh_token'
            }
          );

          const newAccessToken = refreshResponse.data.access_token;
          localStorage.setItem('gmail_token', newAccessToken);
          
          // Retry with new token
          return await sendEmailViaGmail(toEmail, subject, body, attachments);
        } catch (refreshError) {
          localStorage.removeItem("gmail_token");
          localStorage.removeItem("gmail_refresh_token");
          throw new Error("Gmail authentication expired. Please reconfigure in Settings.");
        }
      }
    }
    
    throw new Error('Failed to send email via Gmail API');
  }
};

const sendEmailViaOutlook = async (toEmail, subject, body, attachments = []) => {
  const accessToken = localStorage.getItem("outlook_token");
  
  if (!accessToken) {
    throw new Error("Outlook not configured. Please set up Outlook in Settings.");
  }

  try {
    const response = await axios.post(
      'https://graph.microsoft.com/v1.0/me/sendMail',
      {
        message: {
          subject: subject,
          body: {
            contentType: 'Text',
            content: body
          },
          toRecipients: [
            {
              emailAddress: {
                address: toEmail
              }
            }
          ]
        }
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error('Outlook API Error:', error.response?.data || error.message);
    throw new Error('Failed to send email via Outlook API');
  }
};

// Main email sending function
const sendEmail = async (toEmail, subject, body, provider = 'gmail', attachments = []) => {
  try {
    if (provider === 'gmail') {
      return await sendEmailViaGmail(toEmail, subject, body, attachments);
    } else if (provider === 'outlook') {
      return await sendEmailViaOutlook(toEmail, subject, body, attachments);
    } else {
      throw new Error('Unsupported email provider. Use "gmail" or "outlook".');
    }
  } catch (error) {
    console.error('Email sending failed:', error);
    throw error;
  }
};

// Enhanced email sending with template support
const sendEmailWithTemplate = async (template, variables, toEmail, provider = 'gmail', attachments = []) => {
  if (!template) {
    throw new Error('Email template is required');
  }

  // Process template with variables
  const processedSubject = processTemplate(template.subject, variables);
  const processedBody = processTemplate(template.body, variables);

  // Send email
  return await sendEmail(toEmail, processedSubject, processedBody, provider, attachments);
};

// Driver Approval Request Email Function
const sendDriverApprovalEmail = async (driverRequest, template, user, policyData = null, employeeData = null) => {
  try {
    console.log('📧 sendDriverApprovalEmail called with:');
    console.log('  driverRequest:', driverRequest);
    console.log('  template:', template);
    console.log('  user:', user);
    console.log('  policyData:', policyData);
    
    // Debug the data structure
    console.log('🔍 Data structure analysis:');
    console.log('  driverRequest.employee:', driverRequest.employee);
    console.log('  driverRequest.insurancePolicy:', driverRequest.insurancePolicy);
    console.log('  policyData:', policyData);
    console.log('  employeeData:', employeeData);
    
    // Prepare variables for template - use employeeData and policyData as primary sources
    const variables = {
      // Template variable names (matching your 8base template)
      employeeName: employeeData ? `${employeeData.firstName || employeeData.first_name || ''} ${employeeData.lastName || employeeData.last_name || ''}`.trim() : `${driverRequest.employee?.firstName || driverRequest.employee?.first_name || ''} ${driverRequest.employee?.lastName || driverRequest.employee?.last_name || ''}`.trim(),
      provider: policyData?.provider || driverRequest.insurancePolicy?.provider || 'N/A',
      businessUnitName: policyData?.businessUnits?.name || policyData?.businessUnit?.name || driverRequest.insurancePolicy?.businessUnits?.name || 'N/A',
      employeePosition: employeeData?.position || employeeData?.jobTitle || driverRequest.employee?.position || driverRequest.employee?.jobTitle || 'Employee',
      requestedByName: `${user?.firstName || user?.first_name || 'User'} ${user?.lastName || user?.last_name || ''}`.trim(),
      currentDate: dayjs().format('YYYY-MM-DD'),
      policyNumber: policyData?.policyNumber || policyData?.policy_number || driverRequest.insurancePolicy?.policyNumber || 'N/A',
      
      // Additional variables for backward compatibility
      employeeFullName: employeeData ? `${employeeData.firstName || employeeData.first_name || ''} ${employeeData.lastName || employeeData.last_name || ''}`.trim() : `${driverRequest.employee?.firstName || driverRequest.employee?.first_name || ''} ${driverRequest.employee?.lastName || driverRequest.employee?.last_name || ''}`.trim(),
      employeeEmail: employeeData?.emailAddress || employeeData?.email || driverRequest.employee?.emailAddress || driverRequest.employee?.email || 'N/A',
      policyType: policyData?.coverageType || policyData?.coverage_type || driverRequest.insurancePolicy?.coverageType || 'N/A',
      insuranceCompany: policyData?.provider || policyData?.insuranceProvider || driverRequest.insurancePolicy?.provider || 'N/A',
      agentName: policyData?.agentName || policyData?.agent_name || driverRequest.insurancePolicy?.agentName || 'Insurance Agent',
      agentEmail: policyData?.agentEmail || policyData?.agent_email || driverRequest.insurancePolicy?.agentEmail || 'xopes49335@noidem.com',
      requestDate: dayjs(driverRequest.requestDate).format('YYYY-MM-DD'),
      userFirstName: user?.firstName || user?.first_name || 'User',
      userLastName: user?.lastName || user?.last_name || '',
      requestReason: driverRequest.requestReason || 'Driver approval request'
    };

    console.log('📧 Template variables created:', variables);

    // Send email to insurance agent
    console.log('📧 Driver Approval Email Debug:');
    console.log('  driverRequest:', driverRequest);
    console.log('  insurancePolicy:', driverRequest.insurancePolicy);
    console.log('  agentEmail from request:', driverRequest.insurancePolicy?.agentEmail);
    console.log('  agentEmail from policyData:', policyData?.agentEmail);

    const toEmail = policyData?.agentEmail || driverRequest.insurancePolicy?.agentEmail || 'xopes49335@noidem.com';
    console.log('  Final toEmail:', toEmail);
    console.log('  Template variables:', variables);
    console.log('  Template subject:', template.subject);
    console.log('  Template body:', template.body);
    
    return await sendEmailWithTemplate(template, variables, toEmail, 'gmail');
  } catch (error) {
    console.error('Failed to send driver approval email:', error);
    throw error;
  }
};

// Certificate Request Email Function
const sendCertificateRequestEmail = async (certificateRequest, template, user, policyData = null, clientData = null) => {
  try {
    console.log('📧 sendCertificateRequestEmail called with:');
    console.log('  certificateRequest:', certificateRequest);
    console.log('  template:', template);
    console.log('  user:', user);
    console.log('  policyData:', policyData);
    console.log('  clientData:', clientData);
    
    // Debug the data structure
    console.log('🔍 Certificate Request Data structure analysis:');
    console.log('  certificateRequest.client:', certificateRequest.client);
    console.log('  certificateRequest.insurancePolicy:', certificateRequest.insurancePolicy);
    console.log('  policyData:', policyData);
    console.log('  clientData:', clientData);
    
    // Prepare variables for template - use clientData and policyData as primary sources with fallbacks
    const variables = {
      // Template variable names (matching your 8base template)
      clientFullName: clientData?.name || certificateRequest.client?.name || 'N/A',
      clientEmail: clientData?.email || certificateRequest.client?.email || 'N/A',
      policyNumber: policyData?.policyNumber || policyData?.policy_number || certificateRequest.insurancePolicy?.policyNumber || 'N/A',
      provider: policyData?.provider || certificateRequest.insurancePolicy?.provider || 'N/A',
      policyType: policyData?.coverageType || policyData?.coverage_type || certificateRequest.insurancePolicy?.coverageType || 'N/A',
      insuranceCompany: policyData?.provider || certificateRequest.insurancePolicy?.provider || 'N/A',
      agentName: policyData?.agentName || policyData?.agent_name || certificateRequest.insurancePolicy?.agentName || 'Insurance Agent',
      agentEmail: policyData?.agentEmail || policyData?.agent_email || certificateRequest.insurancePolicy?.agentEmail || 'xopes49335@noidem.com',
      businessUnitName: policyData?.businessUnits?.name || policyData?.businessUnit?.name || certificateRequest.insurancePolicy?.businessUnits?.name || 'N/A',
      requestDate: dayjs(certificateRequest.requestDate).format('YYYY-MM-DD'),
      currentDate: dayjs().format('YYYY-MM-DD'),
      requestedByName: user ? `${user.firstName || user.first_name || ''} ${user.lastName || user.last_name || ''}`.trim() : 'User',
      userFirstName: user?.firstName || user?.first_name || 'User',
      userLastName: user?.lastName || user?.last_name || '',
      certificateNumber: certificateRequest.certificateNumber || 'Pending',
      effectiveDates: certificateRequest.effectiveDates || 'To be determined',
      
      // Additional variables for backward compatibility
      clientName: clientData?.name || certificateRequest.client?.name || 'N/A',
      clientAddress: clientData?.address1 || certificateRequest.client?.address1 || 'N/A',
      clientPhone: clientData?.phone || certificateRequest.client?.phone || 'N/A',
      requestReason: certificateRequest.requestReason || 'Certificate request'
    };

    console.log('📧 Certificate Request Email Debug:');
    console.log('  certificateRequest:', certificateRequest);
    console.log('  insurancePolicy:', certificateRequest.insurancePolicy);
    console.log('  agentEmail from request:', certificateRequest.insurancePolicy?.agentEmail);
    console.log('  agentEmail from policyData:', policyData?.agentEmail);

    const toEmail = policyData?.agentEmail || certificateRequest.insurancePolicy?.agentEmail || 'xopes49335@noidem.com';
    console.log('  Final toEmail:', toEmail);
    console.log('  Template variables:', variables);
    console.log('  Template subject:', template.subject);
    console.log('  Template body:', template.body);
    
    return await sendEmailWithTemplate(template, variables, toEmail, 'gmail');
  } catch (error) {
    console.error('Failed to send certificate request email:', error);
    throw error;
  }
};

// Check if email provider is configured
const isEmailConfigured = (provider = 'gmail') => {
  if (provider === 'gmail') {
    return !!localStorage.getItem("gmail_token");
  } else if (provider === 'outlook') {
    return !!localStorage.getItem("outlook_token");
  }
  return false;
};

// Check if OAuth environment variables are properly configured
const isOAuthConfigured = (provider = 'gmail') => {
  if (provider === 'gmail') {
    const hasClientId = !!process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const hasClientSecret = !!process.env.REACT_APP_GOOGLE_CLIENT_SECRET;
    const hasOrigin = !!window.location.origin;
    
    console.log('Gmail OAuth Configuration Check:', {
      hasClientId,
      hasClientSecret,
      hasOrigin,
      clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID ? 'present' : 'missing',
      liveUrl: window.location.origin
    });
    
    return hasClientId && hasClientSecret && hasOrigin;
  } else if (provider === 'outlook') {
    const hasClientId = !!process.env.REACT_APP_OUTLOOK_CLIENT_ID;
    const hasClientSecret = !!process.env.REACT_APP_OUTLOOK_CLIENT_SECRET;
    const hasOrigin = !!window.location.origin;
    
    return hasClientId && hasClientSecret && hasOrigin;
  }
  return false;
};

// Debug OAuth token process
const debugOAuthToken = () => {
  const token = localStorage.getItem('gmail_token');
  const refreshToken = localStorage.getItem('gmail_refresh_token');
  const userData = localStorage.getItem('gmail_user_data');
  
  const hasClientId = !!process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const hasClientSecret = !!process.env.REACT_APP_GOOGLE_CLIENT_SECRET;
  const hasOrigin = !!window.location.origin;
  
  console.log('=== OAuth Debug Information ===');
  console.log('Environment Variables:');
  console.log('REACT_APP_GOOGLE_CLIENT_ID:', hasClientId ? 'Present' : 'Missing');
  console.log('REACT_APP_GOOGLE_CLIENT_SECRET:', hasClientSecret ? 'Present' : 'Missing');
  console.log('Window Location Origin:', window.location.origin);
  
  console.log('Local Storage Tokens:');
  console.log('gmail_token:', token ? 'Present' : 'Missing');
  console.log('gmail_refresh_token:', refreshToken ? 'Present' : 'Missing');
  console.log('gmail_user_data:', userData ? 'Present' : 'Missing');
  
  console.log('OAuth URLs:');
  const redirectUri = `${window.location.origin}/setting`;
  console.log('Redirect URI:', redirectUri);
  console.log('Current URL:', window.location.href);
  
  const urlParams = new URLSearchParams(window.location.search);
  const authCode = urlParams.get('code');
  const error = urlParams.get('error');
  
  console.log('URL Parameters:');
  console.log('Auth Code:', authCode || 'Missing');
  console.log('Error:', error);
  
  return {
    hasClientId,
    hasClientSecret,
    hasOrigin,
    hasToken: !!token,
    hasRefreshToken: !!refreshToken,
    hasUserData: !!userData,
    redirectUri,
    currentUrl: window.location.href,
    authCode,
    error
  };
};

// Get user data for configured provider
const getEmailUserData = (provider = 'gmail') => {
  if (provider === 'gmail') {
    const userData = localStorage.getItem("gmail_user_data");
    return userData ? JSON.parse(userData) : null;
  } else if (provider === 'outlook') {
    const userData = localStorage.getItem("outlook_user_data");
    return userData ? JSON.parse(userData) : null;
  }
  return null;
};

// Get current user's email address
const getCurrentUserEmail = (provider = 'gmail') => {
  const userData = getEmailUserData(provider);
  return userData?.email || null;
};

export {
  getGmailAccessToken,
  getOutlookAccessToken,
  sendEmail,
  sendEmailViaGmail,
  sendEmailViaOutlook,
  sendEmailWithTemplate,
  sendDriverApprovalEmail,
  sendCertificateRequestEmail,
  processTemplate,
  isEmailConfigured,
  isOAuthConfigured,
  debugOAuthToken,
  getEmailUserData,
  getCurrentUserEmail
};

export default {
  getGmailAccessToken,
  getOutlookAccessToken,
  sendEmail,
  sendEmailViaGmail,
  sendEmailViaOutlook,
  sendEmailWithTemplate,
  sendDriverApprovalEmail,
  sendCertificateRequestEmail,
  processTemplate,
  isEmailConfigured,
  isOAuthConfigured,
  debugOAuthToken,
  getEmailUserData,
  getCurrentUserEmail
};